import React, { useContext, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LanguageContext } from '../hooks/useLanguage';
import { servicesSection } from '../data/content';
import { SHOW } from '../config/visibility';
import ServicePage from './ServicePage';
import { ServiceItem } from '../types';


const Services: React.FC = () => {
  const { lang } = useContext(LanguageContext);
  const [activePage, setActivePage] = useState<string | null>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [current, setCurrent] = useState(0);
  const trackRef = useRef<HTMLDivElement>(null);

  const visibleItems = servicesSection.items.filter((s) => {
    if (s.id === 'electricidad') return SHOW.electricidad;
    if (s.id === 'climatizacion') return SHOW.climatizacion;
    return true;
  }) as ServiceItem[];

  const activeService = servicesSection.items.find(
    (s) => s.id === activePage
  ) as ServiceItem | undefined;

  const VISIBLE = 4;
  const maxIndex = Math.max(0, visibleItems.length - VISIBLE);

  const prev = () => setCurrent((c) => Math.max(0, c - 1));
  const next = () => setCurrent((c) => Math.min(maxIndex, c + 1));

  return (
    <>
      <section id="services" style={{ background: '#1A1A1A', padding: 'clamp(60px, 8vw, 120px) 0' }}>
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            style={{ marginBottom: '56px', display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: '24px' }}
          >
            <div>
              <span style={{
                fontFamily: 'var(--font-body)',
                fontSize: '11px',
                fontWeight: 600,
                letterSpacing: '5px',
                textTransform: 'uppercase' as const,
                color: '#C8A96E',
                display: 'block',
                marginBottom: '12px',
              }}>
                {servicesSection.label[lang]}
              </span>
              <h2 style={{
                fontFamily: 'var(--font-display)',
                fontSize: 'clamp(26px, 3.5vw, 48px)',
                fontWeight: 700,
                letterSpacing: '-0.5px',
                lineHeight: 1.1,
                color: '#FFFFFF',
                maxWidth: '600px',
              }}>
                {servicesSection.title[lang]}
              </h2>
            </div>

            {/* Arrow controls */}
            <div style={{ display: 'flex', gap: '8px' }}>
              {[{ fn: prev, label: '←', disabled: current === 0 }, { fn: next, label: '→', disabled: current === maxIndex }].map(({ fn, label, disabled }) => (
                <button
                  key={label}
                  onClick={fn}
                  disabled={disabled}
                  style={{
                    width: '44px', height: '44px',
                    background: 'transparent',
                    border: `1px solid ${disabled ? '#333' : '#C8A96E'}`,
                    color: disabled ? '#333' : '#C8A96E',
                    fontSize: '16px',
                    cursor: disabled ? 'default' : 'pointer',
                    transition: 'all 0.2s',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </motion.div>

          {/* Slider */}
          <div style={{ overflow: 'hidden' }}>
            <div
              ref={trackRef}
              style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${visibleItems.length}, calc(25% - 1.5px))`,
                gap: '2px',
                transform: `translateX(calc(-${current} * (25%)))`,
                transition: 'transform 0.5s cubic-bezier(0.22,1,0.36,1)',
              }}
            >
              {visibleItems.map((service, i) => (
                <div
                  key={service.id}
                  id={service.id}
                  onClick={() => setActivePage(service.id)}
                  onMouseEnter={() => setHoveredId(service.id)}
                  onMouseLeave={() => setHoveredId(null)}
                  style={{
                    background: '#2D2D2D',
                    overflow: 'hidden',
                    cursor: 'pointer',
                    position: 'relative' as const,
                    borderTop: hoveredId === service.id ? '2px solid #C8A96E' : '2px solid transparent',
                    transition: 'border-color 0.3s',
                  }}
                >
                  <div style={{ overflow: 'hidden', height: '200px' }}>
                    <motion.img
                      src={service.image}
                      alt={service.title[lang]}
                      whileHover={{ scale: 1.04 }}
                      transition={{ duration: 0.6 }}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' as const }}
                    />
                  </div>
                  <div style={{ padding: '28px 24px' }}>
                    <h3 style={{
                      fontFamily: 'var(--font-display)',
                      fontSize: '15px',
                      fontWeight: 700,
                      color: '#FFFFFF',
                      marginBottom: '10px',
                      lineHeight: 1.3,
                    }}>
                      {service.title[lang]}
                    </h3>
                    <p style={{
                      fontFamily: 'var(--font-body)',
                      fontSize: '13px',
                      color: '#AAAAAA',
                      lineHeight: 1.7,
                    }}>
                      {service.description[lang]}
                    </p>
                    <div style={{
                      marginTop: '20px',
                      fontFamily: 'var(--font-body)',
                      fontSize: '11px',
                      fontWeight: 600,
                      letterSpacing: '3px',
                      textTransform: 'uppercase' as const,
                      color: hoveredId === service.id ? '#C8A96E' : '#555555',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      transition: 'color 0.3s',
                    }}>
                      {lang === 'es' ? 'Ver más' : lang === 'en' ? 'Read more' : 'En savoir plus'}
                      <span>→</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Dots */}
          <div style={{ display: 'flex', gap: '8px', marginTop: '32px', justifyContent: 'center' }}>
            {Array.from({ length: maxIndex + 1 }).map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrent(i)}
                style={{
                  width: i === current ? '24px' : '6px',
                  height: '6px',
                  background: i === current ? '#C8A96E' : '#333',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                  transition: 'all 0.3s',
                }}
              />
            ))}
          </div>
        </div>
      </section>

      <AnimatePresence>
        {activePage && activeService && (
          <ServicePage
            service={activeService}
            lang={lang}
            onClose={() => setActivePage(null)}
          />
        )}
      </AnimatePresence>
    </>
  );
};


export default Services;
